import { AutoComplete } from 'antd';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@mui/material';

import { financeWithdrawalHttp, http } from '@network';
import { CreditInvoice, DealerType, FinanceWithdrawal } from '@types';
import { AntdDateControlledInput, ErrorMessage, InputText } from '@components';
import * as zipCodes from '@utils/zip-codes.json';
import cls from './credit-invoice-company.module.css';

interface Props {
  retailerName: string;
  zipState?: string | null;
  xmlUuid?: string | null;
  xmlDate?: string | null;
  updateValues: (e: ChangeEvent<HTMLInputElement>) => void;
  showErrors?: boolean;
  invoice?: CreditInvoice;
  submit?: (creditId?: number) => Promise<void | CreditInvoice | null>;
}

interface CheckOption {
  value: string,
  label: string,
}

export const CreditInvoiceCompany = ({
  retailerName, zipState, xmlUuid, xmlDate, updateValues, showErrors, invoice, submit,
}: Props) => {
  const showRetailerError = !retailerName && showErrors;
  const showStateError = !zipState && showErrors;
  const [retailers, setRetailers] = useState<CheckOption[]>([]);
  const [states, setStates] = useState<CheckOption[]>([]);
  const [withdrawals, setWithdrawals] = useState<FinanceWithdrawal[]>([]);

  const onChangeRetailer = (e: string): void => {
    updateValues({ target: { name: 'retailerName', value: e } } as any);
  };
  const onChangeState = (e: string): void => {
    updateValues({ target: { name: 'zipState', value: e } } as any);
  };
  const onChangeUuid = (e: string | null): void => {
    updateValues({ target: { name: 'xmlUuid', value: e } } as any);
  };
  const onChangeDate = (e: string | null): void => {
    updateValues({ target: { name: 'xmlDate', value: e } } as any);
  };

  const getStates = async () => {
    const nextStates: CheckOption[] = [];
    for (const [zip, state] of Object.entries(zipCodes)) {
      const zipCode = [zip, state].filter(Boolean).join(' - ');
      const option: CheckOption = {
        value: zipCode,
        label: zipCode,
      };
      nextStates.push(option);
    }
    setStates(nextStates);
  };

  const getRetailers = async () => {
    const response = await http.get<any>(`dealer/by-type/${DealerType.RETAILER}`, {
      params: { _start: 0, _end: 9999999 },
    });
    const nextOptions: CheckOption[] = [];
    if (response.data && Array.isArray(response.data)) {
      (response.data ?? []).forEach((model: any) => {
        nextOptions.push({ value: model['name'], label: model['name'] });
      });
    }
    setRetailers(nextOptions);
  };

  const getFinanceWithdrawals = async () => {
    if (!xmlUuid) {
      return;
    }

    const nextWithdrawals = await financeWithdrawalHttp.getByUuid(xmlUuid);
    if (nextWithdrawals) {
      setWithdrawals(nextWithdrawals);
    }
  };

  useEffect(() => {
    getStates();
    getRetailers();
    getFinanceWithdrawals();
  }, []);

  useEffect(() => {
    getFinanceWithdrawals();
  }, [xmlUuid]);

  const str = (s?: string) => `${s || ''}`.toLowerCase()
    .normalize('NFD').replace(/\p{Diacritic}/gu, '');
  const retailersOptions = retailers.filter(o => str(o.label).includes(str(retailerName)));
  const statesOptions = states.filter(s => str(s.label).includes(str(zipState ?? '')));
  const isXml = invoice?.isXml ? true : false;

  return (
    <>
      <div className={cls.image}>
        <InvoiceIcon />
      </div>
      <div className={cls.rows}>
        <div className={cls.row}>
          <AutoComplete
            className={cls.autocomplete}
            onChange={onChangeRetailer}
            placeholder="Retail Store"
            options={retailersOptions}
            allowClear
            value={retailerName}
          />
          <div className={cls.extraction}>
            CFDI Emisor [Nombre]
            {invoice?.xmlCompany && <b>- {invoice?.xmlCompany}</b>}
          </div>
          {showRetailerError && (
            <ErrorMessage className={cls.err} shrink text="This filed is requred" />
          )}
        </div>
        <div className={cls.row}>
          <AutoComplete
            className={cls.autocomplete}
            onChange={onChangeState}
            placeholder="ZIP Code"
            options={statesOptions}
            allowClear
            value={zipState}
          />
          <div className={cls.extraction}>Lugar Expedición</div>
          {showStateError && (
            <ErrorMessage className={cls.err} shrink text="This filed is requred" />
          )}
        </div>
        <div className={cls.row}>
          <AntdDateControlledInput
            valueStr={typeof xmlDate === 'string' ? xmlDate : null}
            onUpdate={onChangeDate}
            disabled={isXml}
          />
          <div className={cls.extraction} style={{ paddingTop: 8 }}>Fecha Timbrado</div>
        </div>
        <div className={cls.row}>
          <InputText
            name="xmlUuid"
            label=""
            withoutLabel
            placeholder="UUID"
            value={xmlUuid ?? ''}
            onChange={e => onChangeUuid(e.target.value)}
            disabled={isXml}
          />
          <div className={cls.extraction}>UUID</div>
          {withdrawals.length > 0 && (
            <div className={cls.list}>
              {withdrawals.map(withdrawal => (
                <div key={withdrawal.id} className={cls.item}>
                  <Link to={`/finance-withdrawal/${withdrawal.id}`} target="_blank">
                    Financial withdrawal: {withdrawal.code}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={cls.actions}>
          <Button onClick={() => submit?.()} className={cls.submit} variant="contained" type="button">Save</Button>
        </div>
      </div>
    </>
  );
};

const InvoiceIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg"
    width="102"
    height="102"
    viewBox="0 0 102 102"
    fill="none">
    <g clipPath="url(#clip0_4195_4875)">
      <path d="M51 102C79.1665 102 102 79.1665 102 51C102 22.8335 79.1665 0 51 0C22.8335 0 0 22.8335 0 51C0 79.1665 22.8335 102 51 102Z"
        style={{ fill: 'var(--primary-color)' }} />
      <path d="M57.4869 38.3097C55.5864 38.2783 53.733 38.9379 52.2722 40.1474C51.0471 41.6081 50.3874 43.4615 50.4188 45.362C52.3194 45.3934 54.1728 44.7337 55.6335 43.5243C56.8586 42.0636 57.5183 40.2102 57.4869 38.3097Z"
        fill="white" />
      <path d="M48.2199 45.362C48.1728 44.4353 47.8272 43.5557 47.2304 42.8489C46.5236 42.252 45.644 41.8908 44.7173 41.8594C44.7487 42.7704 45.0942 43.6657 45.6754 44.3568C46.3822 44.9693 47.2775 45.3306 48.2199 45.362Z"
        fill="white" />
      <path d="M60.2042 53.8438H41.8901C41.3874 53.8438 40.9791 54.2521 40.9791 54.7547V62.0741C40.9791 62.5767 41.3874 62.9851 41.8901 62.9851H60.2042C60.7068 62.9851 61.1152 62.5767 61.1152 62.0741V54.7547C61.1152 54.2521 60.7068 53.8438 60.2042 53.8438ZM43.7278 55.6814H49.2251C49.7278 55.6814 50.1361 56.0898 50.1361 56.5924C50.1361 57.0951 49.7278 57.5034 49.2251 57.5034H43.7278C43.2251 57.5034 42.8168 57.0951 42.8168 56.5924C42.8168 56.0898 43.2251 55.6814 43.7278 55.6814ZM45.5655 61.1788H43.7278C43.2251 61.1788 42.8168 60.7705 42.8168 60.2678C42.8168 59.7652 43.2251 59.3568 43.7278 59.3568H45.5655C46.0681 59.3568 46.4764 59.7652 46.4764 60.2678C46.4764 60.7705 46.0681 61.1788 45.5655 61.1788ZM58.3822 61.1788H49.2251C48.7225 61.1788 48.3141 60.7705 48.3141 60.2678C48.3141 59.7652 48.7225 59.3568 49.2251 59.3568H58.3822C58.8848 59.3568 59.2932 59.7652 59.2932 60.2678C59.2932 60.7705 58.8848 61.1788 58.3822 61.1788ZM58.3822 57.5191H52.8848C52.3822 57.5191 51.9738 57.1108 51.9738 56.6082C51.9738 56.1055 52.3822 55.6972 52.8848 55.6972H58.3822C58.8848 55.6972 59.2932 56.1055 59.2932 56.6082C59.2932 57.1108 58.8848 57.5191 58.3822 57.5191Z"
        fill="white" />
      <path d="M31.8534 68.45C29.2618 56.9683 29.2618 45.0469 31.8534 33.5495C31.7906 33.471 31.7277 33.3924 31.6649 33.3139C30 33.5809 28.3665 33.9579 26.7487 34.4291C26.3717 34.539 25.9633 34.4291 25.6806 34.1463C25.6806 34.1463 25.6021 34.0678 25.6021 34.0521C23.8743 32.3244 22.0995 31.9003 21.7382 32.2615C21.377 32.6228 21.801 34.3977 23.5288 36.1254L23.623 36.2039C23.89 36.4552 24 36.8479 23.9215 37.2092C22.7906 42.0626 22.2565 47.0416 22.3194 52.0207C22.5078 55.6804 23.089 61.4448 23.9215 65.0102C24 65.3715 23.89 65.7484 23.623 66.0155C23.623 66.0155 23.5445 66.094 23.5288 66.1097C21.801 67.8217 21.3927 69.6123 21.7382 69.9736C21.8796 70.1149 22.3194 70.1463 22.9633 69.9422C23.9686 69.5652 24.8639 68.9683 25.6021 68.1987L25.6806 68.1045C25.9476 67.8217 26.356 67.7118 26.7173 67.8217C28.3037 68.2458 29.9058 68.5914 31.5393 68.827C31.6492 68.7013 31.7435 68.5757 31.8534 68.45Z"
        fill="white" />
      <path d="M68.356 69.5506C68.089 69.2993 67.9791 68.9066 68.0576 68.5453C70.8377 57.0165 70.8377 44.9851 68.0576 33.4563C67.9633 33.0951 68.0733 32.7024 68.356 32.4354L68.4503 32.3412C69.4555 31.383 70.2408 30.205 70.7277 28.9014C71.0105 28.0689 70.9948 27.3621 70.712 27.0794C70.0995 26.4668 67.7435 27.048 65.466 29.3254L65.356 29.4354C65.0733 29.7181 64.6649 29.8281 64.288 29.7181C55.6649 27.048 46.445 27.048 37.8063 29.7181C37.4293 29.8281 37.0052 29.7181 36.7382 29.4354C36.7382 29.4354 36.6597 29.3569 36.644 29.3412C35.4817 27.9904 33.8796 27.1108 32.1204 26.8438C31.8534 26.8281 31.5864 26.9066 31.3822 27.0637C31.0995 27.3464 31.0838 28.0532 31.3665 28.8857C31.8534 30.1893 32.6387 31.3516 33.6283 32.3097L33.7382 32.4197C34.0052 32.671 34.1152 33.0637 34.0366 33.4249C31.3508 44.9694 31.3508 56.9851 34.0366 68.5296C34.1152 68.8909 34.0052 69.2678 33.7382 69.5349C33.7382 69.5349 33.6597 69.6134 33.644 69.6291C31.3351 71.938 30.7696 74.2783 31.3822 74.8909C31.9948 75.5034 34.3508 74.9223 36.6283 72.6448L36.7382 72.5349C37.0052 72.2521 37.4136 72.1422 37.7749 72.2521C46.4607 74.6553 55.6335 74.6553 64.3037 72.2521C64.6806 72.1579 65.0733 72.2521 65.3403 72.5349L65.4345 72.6291C67.7435 74.938 70.0838 75.5034 70.6963 74.8909C71.3089 74.2783 70.7277 71.9223 68.4503 69.6448L68.3403 69.5349L68.356 69.5506ZM42.5497 40.6972C42.5969 40.1788 43.0052 39.7705 43.5079 39.7391C43.6492 39.7391 46.822 39.4406 48.6597 41.3412C48.7539 41.4354 48.8325 41.5296 48.9267 41.6396C49.3037 40.5244 49.9162 39.5192 50.733 38.671C53.5759 35.8281 58.3979 36.2364 58.5864 36.2364C59.1047 36.2836 59.5131 36.6919 59.5445 37.1946C59.5445 37.3987 59.9843 42.205 57.1256 45.0637C54.9267 47.2626 51.5497 47.5296 50.0419 47.5296H48.8482C47.1361 47.5925 45.4555 47.0113 44.1518 45.8804C42.2513 43.9956 42.5183 40.8385 42.5183 40.6972H42.5497ZM62.9686 63.0008C62.9686 64.0061 62.1518 64.8385 61.1309 64.8385H40.9948C39.9895 64.8385 39.1571 64.0218 39.1571 63.0008V53.8438C39.1571 52.8385 39.9738 52.0061 40.9948 52.0061H61.1309C62.1361 52.0061 62.9686 52.8228 62.9686 53.8438V63.0008Z"
        fill="white" />
      <path d="M78.377 66.0318C78.1099 65.7805 78 65.4035 78.0942 65.0423C79.4136 59.4349 79.9476 53.6705 79.6806 47.9218C79.4293 45.0475 78.801 40.0527 78.1099 37.2569C78.0157 36.8957 78.1256 36.503 78.3927 36.2517C78.4084 36.236 78.4712 36.1731 78.4869 36.1574C80.2147 34.4297 80.623 32.6548 80.2775 32.2936C80.1361 32.1522 79.6963 32.1208 79.0524 32.325C78.0471 32.7019 77.1518 33.2988 76.4136 34.0684L76.3194 34.1627C76.0366 34.4454 75.6283 34.5396 75.2513 34.4297C73.6806 33.9585 72.0628 33.5815 70.445 33.3145C70.3822 33.3773 70.3351 33.4559 70.2722 33.5187C72.9424 45.0004 72.9424 56.9375 70.2722 68.4192C70.3822 68.5292 70.4607 68.6548 70.5707 68.7648C72.1571 68.5292 73.7434 68.1993 75.2984 67.7752C75.6754 67.6653 76.0681 67.7752 76.3351 68.058L76.4136 68.1522C78.1414 69.8799 79.9162 70.2883 80.2775 69.9428C80.6387 69.5972 80.2147 67.8066 78.4869 66.0789L78.3927 66.0004L78.377 66.0318Z"
        fill="white" />
    </g>
    <defs>
      <clipPath id="clip0_4195_4875">
        <rect width="102" height="102" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
